import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of, throwError } from 'rxjs';
import { BASE_URL_TOKEN, Agreement, AgreementType } from '@raptor-fe/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class AgreementService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = inject(BASE_URL_TOKEN);
    private readonly agreementPath = (tenantId: string) => `${this.baseUrl}v1/tenant/${tenantId}/agreement`;

    /**
     * Gets the active agreement for a specific type. Returns null when the backend
     * responds with 404 (no active agreement configured for this tenant/type) so
     * callers can treat "not configured" as a valid state instead of an error.
     */
    getActiveAgreement(tenantId: string, type: AgreementType): Observable<Agreement | null> {
        const params = new HttpParams().set('type', type);
        return this.http.get<Agreement | null>(
            `${this.agreementPath(tenantId)}/active`,
            { params }
        ).pipe(
            catchError((error: HttpErrorResponse) => error.status === 404 ? of(null) : throwError(() => error))
        );
    }

    /**
     * Gets all agreements with optional filters
     */
    getAgreements(
        tenantId: string,
        type?: AgreementType,
        activeOnly?: boolean
    ): Observable<Agreement[]> {
        let params = new HttpParams();
        if (type) params = params.set('type', type);
        if (activeOnly !== undefined) params = params.set('activeOnly', activeOnly.toString());

        return this.http.get<Agreement[]>(
            this.agreementPath(tenantId),
            { params }
        );
    }

    /**
     * Gets a specific agreement by ID
     */
    getById(tenantId: string, agreementId: string): Observable<Agreement> {
        return this.http.get<Agreement>(
            `${this.agreementPath(tenantId)}/${agreementId}`
        );
    }

    /**
     * Creates a new agreement
     */
    create(tenantId: string, agreement: Agreement): Observable<Agreement> {
        return this.http.post<Agreement>(
            this.agreementPath(tenantId),
            agreement
        );
    }

    /**
     * Updates an existing agreement
     */
    update(tenantId: string, agreement: Agreement): Observable<Agreement> {
        return this.http.put<Agreement>(
            this.agreementPath(tenantId),
            agreement
        );
    }

    /**
     * Soft deletes an agreement
     */
    delete(tenantId: string, agreementId: string): Observable<void> {
        return this.http.delete<void>(
            `${this.agreementPath(tenantId)}/${agreementId}`
        );
    }

    /**
     * Gets version history for an agreement type
     */
    getVersionHistory(tenantId: string, type: AgreementType): Observable<Agreement[]> {
        const params = new HttpParams().set('type', type);
        return this.http.get<Agreement[]>(
            `${this.agreementPath(tenantId)}/history`,
            { params }
        );
    }
}
